import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const portal = (node) => createPortal(node, document.body);
import { Plane, FastForward, ShieldAlert, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
export async function flightCall(gameId, path, body) {
  const res = await fetch(`${API}/api/game/${gameId}/${path}`, body !== undefined
    ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : undefined);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

export const IMG = {
  terminal: '/flights/terminal.jpg', cabin: '/flights/cabin.jpg', customs: '/flights/customs.jpg',
  brooklyn: '/rtb/cities/bg_brooklyn.jpg',
  colombia: '/flights/colombia.jpg', peru: '/flights/peru.jpg', mexico: '/flights/mexico.jpg',
};
export const countryImg = (country) => IMG[(country || '').toLowerCase()] || IMG.cabin;
export const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`;

/** Full-screen cinematic sequence. Pure presentation: never touches game state. */
export function Cinematic({ steps, onDone, testId = 'flight-cinematic' }) {
  const [i, setI] = useState(0);
  const done = useRef(false);
  const finish = () => { if (!done.current) { done.current = true; onDone(); } };
  useEffect(() => {
    if (i >= steps.length) { finish(); return undefined; }
    const id = setTimeout(() => setI((n) => n + 1), steps[i].dur || 2600);
    return () => clearTimeout(id);
  }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = steps[Math.min(i, steps.length - 1)];
  return portal(
    <div data-testid={testId} data-step={i} className="fixed inset-0 z-[3000] bg-black overflow-hidden">
      {steps.map((st, k) => (
        <div key={k} className={`absolute inset-0 bg-cover bg-center fl-kenburns transition-opacity duration-700 ${k === Math.min(i, steps.length - 1) ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundImage: `url('${st.img}')` }} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/60" />
      <div className="absolute inset-x-0 top-0 h-[9vh] bg-black" /><div className="absolute inset-x-0 bottom-0 h-[9vh] bg-black" />
      <div key={i} className="absolute left-6 sm:left-14 bottom-[16vh] max-w-2xl fl-rise">
        <p className="text-[10px] sm:text-xs tracking-[0.4em] text-[#C9A84C]">{s.kicker}</p>
        <h2 className="mt-2 font-['Bebas_Neue',sans-serif] text-4xl sm:text-6xl lg:text-7xl text-white leading-none">{s.title}</h2>
        {s.sub && <p className="mt-3 text-sm sm:text-base text-white/75 max-w-xl">{s.sub}</p>}
        {s.route && (
          <div className="mt-6 w-[min(80vw,520px)]">
            <div className="flex justify-between text-[10px] tracking-[0.3em] text-white/60"><span>{s.route[0]}</span><span>{s.route[1]}</span></div>
            <div className="relative mt-2 h-px bg-white/25">
              <div className="absolute inset-y-0 left-0 bg-[#C9A84C] fl-progress" style={{ animationDuration: `${s.dur || 2600}ms` }} />
              <Plane size={18} className="absolute -top-[9px] text-[#F3DC8A] fl-plane" style={{ animationDuration: `${s.dur || 2600}ms` }} />
            </div>
          </div>
        )}
      </div>
      <button type="button" data-testid="flight-skip-btn" onClick={finish}
        className="absolute right-4 top-[11vh] flex items-center gap-1.5 rounded-full border border-white/30 bg-black/50 px-4 py-2 text-xs tracking-widest text-white hover:border-[#C9A84C] transition-colors">
        SKIP <FastForward size={14} />
      </button>
    </div>
  );
}

/** Cinematic decision card for encounters, police intercepts and customs. */
export function DecisionCard({ img, kicker, title, narrative, stats, choices, onChoose, busy, result, onContinue, testId, error }) {
  return portal(
    <div data-testid={testId} className="fixed inset-0 z-[2900] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-3">
      <div className="fl-rise w-full max-w-2xl overflow-hidden rounded-2xl border border-[#C9A84C]/40 bg-[#0a0908]">
        <div className="relative h-44 sm:h-60 bg-cover bg-center" style={{ backgroundImage: `url('${img}')` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] to-transparent" />
          <div className="absolute left-5 bottom-3">
            <p className="flex items-center gap-1.5 text-[10px] tracking-[0.35em] text-[#F87171]"><ShieldAlert size={12} /> {kicker}</p>
            <h3 className="font-['Bebas_Neue',sans-serif] text-3xl sm:text-4xl text-white leading-none">{title}</h3>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-white/80">{narrative}</p>
          {error && !result && <p data-testid={`${testId}-error`} className="rounded-lg border border-[#F87171]/50 bg-[#7f1d1d]/40 px-3 py-2 text-xs text-[#FECACA]">{error}</p>}
          {stats && <div className="flex flex-wrap gap-2">{stats.map(([k, v]) => <span key={k} className="rounded-md bg-white/5 border border-white/10 px-2.5 py-1 text-xs text-white/70">{k}: <b className="text-white">{v}</b></span>)}</div>}
          {result ? (
            <div data-testid={`${testId}-result`} className={`rounded-lg border p-3 text-sm ${result.success ? 'border-[#4ADE80]/50 text-[#BBF7D0]' : 'border-[#F87171]/50 text-[#FECACA]'}`}>
              {result.narrator}
              <button type="button" data-testid={`${testId}-continue`} onClick={onContinue} className="mt-3 block w-full rounded-full bg-[#C9A84C] py-2.5 text-sm font-semibold tracking-widest text-black">CONTINUE</button>
            </div>
          ) : (
            <div className="grid gap-2">
              {choices.map((c) => (
                <button key={c.id} type="button" data-testid={`${testId}-choice-${c.id}`} disabled={busy || c.disabled} onClick={() => onChoose(c.id)}
                  className="group text-left rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 hover:border-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors disabled:opacity-50">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white">{busy && <Loader2 size={14} className="animate-spin" />}{c.label}</div>
                  {c.desc && <div className="text-xs text-white/55 mt-0.5">{c.desc}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
